import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import type { TransitionProps } from '@mui/material/transitions';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import anneFrankAvatar from '../../assets/icons/annefrankAvatar.webp';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectCurrentLanguage, selectDirection, selectLayout } from '../../redux/selectors';
import type { Place } from '../../types/place';
import { getPlace } from '../../services/data';
import { useAsyncData } from '../../hooks/useAsyncData';
import ImageWrapper from '../../components/common/ImageWrapper'
import { setFooterNavigate } from '../../redux/actions/appSettingsActions';
import AudioPlayer from '../../components/players/LegacyAudioPlayer';
import WalkingAudioPlayer from '../../components/players/WalkingAudioPlayer';
import TextWidget from '../../components/widgets/TextWidget';
import ChatWidget from '../../components/widgets/ChatWidget';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import VideoPlayer from '../../components/players/VideoPlayer';
import SanitizedParagraph from '../../components/common/SanitizedParagraph';
import StationLinks from '../../components/common/StationLinks';
import { setMapData } from '../../redux/actions/mapActions';
import { createImageUrl } from '../../services/data';
import { addDefaultSource } from '../../services/errorHandlers';
import { palette } from '../../themes/theme';
import { APP_ANNEFRANK, APP_ANNEFRANK_REV, ANNEFRANK_CHAT_CONTEXTS } from '../../constants/apps';
import { ANNEFRANK_CHAT_URL } from '../../config';

const SlideUp = React.forwardRef(function SlideUp(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface StationPageV2Props {
    id?: string;
    initialPlace?: Place | null;
}

const StationPageV2: React.FC<StationPageV2Props> = ({id, initialPlace}) => {
    const { t } = useTranslation()
    const currentLanguage = useAppSelector(selectCurrentLanguage)
    const direction = useAppSelector(selectDirection)
    const {placeId: routePlaceId, slug} = useParams()
    const layout = useAppSelector(selectLayout)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const [chatOpen, setChatOpen] = useState(false)

    const from = (location.state as { from?: string })?.from || ''
    
    const placeId = routePlaceId || id
    const isAnneFrankTour = slug === APP_ANNEFRANK || slug === APP_ANNEFRANK_REV;
    const showChatFab = isAnneFrankTour && !!ANNEFRANK_CHAT_URL;

    const { data: fetchedStation, isLoading: fetchLoading, error, isNetworkError: isNetworkErr, refetch } = useAsyncData<Place | null>(
        () => !initialPlace && placeId ? getPlace(placeId, currentLanguage ?? 'en') : Promise.resolve(null),
        [currentLanguage, placeId, id, !initialPlace]
    );

    const station = initialPlace ?? fetchedStation;
    const isLoading = initialPlace ? false : fetchLoading;

    useEffect(() => {
        //if (from !== 'keypad' || from === '') {
            dispatch(setFooterNavigate(true))
        //}
        
        return () => { dispatch(setFooterNavigate(false)) }
    }, [dispatch, slug, from])

    const handleMapClick = () => {
        dispatch(setMapData({
            coordinates: station?.location?.coordinates ?? null,
            properties: { 
                name: station?.translations[0]?.information_title ?? '', 
                title: station?.translations[0]?.information_title ?? '',
                information_title: station?.translations[0]?.information_title ?? '',
                information_subtitle: station?.translations[0]?.information_subtitle ?? ''
            }
        }))
        navigate(`/${slug}/map`)
    }

    if (error) {
        return (
            <Box sx={{ direction, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
                <Typography variant='h2' sx={{ mb: 2 }}>{isNetworkErr ? '📡' : '⚠'}</Typography>
                <Typography variant='body1' sx={{ textAlign: 'center', mb: 2 }}>
                    {isNetworkErr ? t('common.error-network') : error}
                </Typography>
                <Button variant="outlined" onClick={refetch}>{t('common.try-again')}</Button>
            </Box>
        );
    }

    if (isLoading && layout === 'walking') {
        return (
            <Box sx={{ background: palette.background.white, p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: 500, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="rounded" width={120} height={28} />
                    </Box>
                    <Skeleton variant="text" width="80%" height={36} sx={{ mt: 1 }} />
                    <Skeleton variant="text" width="60%" height={20} />
                    <Skeleton variant="text" width="40%" height={20} />
                    <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2, borderRadius: 1 }} />
                    <Skeleton variant="rectangular" width="100%" height={48} sx={{ mt: 2, borderRadius: 1 }} />
                    <Skeleton variant="text" width="100%" height={18} sx={{ mt: 2 }} />
                    <Skeleton variant="text" width="100%" height={18} />
                    <Skeleton variant="text" width="70%" height={18} />
                </Box>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{ direction, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
                <Skeleton variant="text" width="60%" height={40} />
                <Skeleton variant="rectangular" width="100%" height={300} sx={{ mt: 2, borderRadius: 1 }} />
                <Skeleton variant="text" width="100%" height={20} sx={{ mt: 2 }} />
                <Skeleton variant="text" width="100%" height={20} />
            </Box>
        );
    }

    if (layout === 'walking') {
        return (
            <Box
                sx={{
                    background: palette.background.white,
                    p: { xs: 2, sm: 3 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 500, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* תגית עגולה */}
                    {/* <Box
                        sx={{
                            display: 'inline-block',
                            background: '#F5F4EF',
                            color: '#79828E',
                            borderRadius: '10px',
                            px: 1.3,
                            py: 1.1,
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '16px',
                            letterSpacing: 0,
                            minWidth: 0,
                            textAlign: 'center',
                            boxShadow: 'none',
                            mb: 0.5,
                            alignSelf: 'flex-start',
                        }}
                    >
                        {station?.translations[0].information_title}
                    </Box> */}

                    <Box sx={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                        <img src={createImageUrl(station?.information_icon?.id)} alt="icon" style={{width: '2rem'}} onError={addDefaultSource} />
                        <Typography 
                        variant="tour2"
                        sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 600,
                            fontSize: '16px',
                            lineHeight: '16px',
                            color: palette.brand.gold,
                            backgroundColor: palette.background.cream,
                            padding: '0.5rem 1rem',
                            borderRadius: '1rem'
                        }}>
                            {station?.translations[0].information_title}
                        </Typography>
                    </Box>

                    {/* כותרת עבה */}
                    <Typography
                        variant="h2"
                        sx={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 600,
                            color: palette.text.darkest,
                            mt: 1,
                            mb: 1,
                        }}
                    >
                        {station?.translations[0].information_subtitle}
                    </Typography>
                    {/* שורה עם אייקון מיקום + תיאור קצר + כתובת */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.2, gap: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '15px',
                                    letterSpacing: 0,
                                    color: 'black',
                                    whiteSpace: 'pre-line',
                                    flex: 1,
                                }}
                            >
                                {station?.translations[0].information_short_description}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        lineHeight: '15px',
                                        letterSpacing: 0,
                                        color: 'black',
                                        whiteSpace: 'pre-line',
                                        flex: 1,
                                    }}
                                >
                                    {station?.translations[0].information_address}
                                </Typography>
                                <Typography
                                    component="a"
                                    sx={{
                                        color: palette.primary.blue,
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        lineHeight: '15px',
                                        letterSpacing: 0,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        mt: 1,
                                        borderRadius: 0,
                                        opacity: 1
                                    }}
                                    onClick={handleMapClick}
                                >
                                    {t('common.walking-tour.take-me-to-station')}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {
                    station?.translations[0].video && 
                    (
                        <Box sx={{ mt: 2, width: '100%', maxWidth: 500, mx: 'auto', p: 0 }}>
                            <VideoPlayer 
                                withClose={true} 
                                video={station?.translations[0].video.id} 
                                withHorizontal={true} 
                                withOverlay={true}
                                aspectRatio={'16/9'}
                            />
                        </Box>
                    )
                }
                {
                    station?.translations[0].audio && 
                    (
                        <Box sx={{ mt: 2, width: '100%', maxWidth: 500, mx: 'auto', p: 0 }}>
                            <WalkingAudioPlayer audio={station?.translations[0].audio.id} name={station?.translations[0].audio.title ?? ''}/>
                        </Box>
                    )
                }
                {station?.translations[0].information_description && (
                    <Box sx={{ width: '100%', maxWidth: 500, mx: 'auto', mt: 2 }}>
                        <SanitizedParagraph
                            content={station?.translations[0].information_description}
                            fontFamily="Poppins, sans-serif"
                            fontWeight={400}
                            fontSize="16px"
                            lineHeight="24px"
                            textAlign="start"
                            color={palette.text.primary}
                        />
                    </Box>
                )}
                {station?.translations[0].links && station.translations[0].links.length > 0 && (
                    <Box sx={{ width: '100%', maxWidth: 500, mx: 'auto' }}>
                        <StationLinks links={station.translations[0].links} direction={direction} />
                    </Box>
                )}

                {showChatFab && (
                    <>
                        <Fab
                            variant="extended"
                            aria-label="Talk with Anne's Diary"
                            onClick={() => setChatOpen(true)}
                            sx={{
                                position: 'fixed',
                                bottom: 80,
                                right: 20,
                                backgroundColor: palette.brand.gold,
                                color: '#fff',
                                zIndex: 1000,
                                '&:hover': { backgroundColor: '#9A8259' },
                                boxShadow: '0 4px 12px rgba(180, 153, 109, 0.4)',
                                borderRadius: '28px',
                                gap: 1,
                                px: 2,
                                textTransform: 'none',
                            }}
                        >
                            <Box
                                component="img"
                                src={anneFrankAvatar}
                                alt="Anne Frank"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#fff',
                                    whiteSpace: 'nowrap',
                                    fontSize: '13px',
                                }}
                            >
                                Talk with Anne's Diary
                            </Typography>
                        </Fab>

                        <Dialog
                            fullScreen
                            open={chatOpen}
                            onClose={() => setChatOpen(false)}
                            TransitionComponent={SlideUp}
                            sx={{
                                '& .MuiDialog-paper': {
                                    backgroundColor: palette.background.white,
                                    height: '100dvh',
                                    maxHeight: '100dvh',
                                },
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                                borderBottom: `1px solid ${palette.border.light}`,
                                backgroundColor: palette.background.white,
                            }}>
                                <Typography sx={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    color: palette.brand.gold,
                                }}>
                                    {t('common.anne-frank-chat.chat-with-anne')}
                                </Typography>
                                <IconButton onClick={() => setChatOpen(false)} aria-label="close">
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', pb: 2 }}>
                                <ChatWidget
                                    mode="annefrank"
                                    stationId={placeId || ''}
                                    stationName={station?.translations[0]?.information_title || ''}
                                    appSlug={slug || ''}
                                    language={currentLanguage || 'en'}
                                    stationContext={
                                        ANNEFRANK_CHAT_CONTEXTS[station?.translations[0]?.information_title ?? '']
                                        || `The user is at ${station?.translations[0]?.information_title ?? ''} — ${station?.translations[0]?.information_subtitle ?? ''}, on the Anne Frank walking tour in Amsterdam.`
                                    }
                                    welcomeMessage={t('common.anne-frank-chat.welcome')}
                                />
                            </Box>
                        </Dialog>
                    </>
                )}
            </Box>
        )
    }

    return (
        <Box className='galleries-page page' sx={{ direction, display: 'flex', flexDirection: 'column', height: '100%', gap: '1rem' }}>
            <Typography variant='h1'>
                {station?.translations[0].information_title}
            </Typography>
            {
                station?.translations[0].information_subtitle &&
                <Typography variant='body1' sx={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
                    {station?.translations[0].information_subtitle}
                </Typography>
            }

            <List sx={{ overflowY: 'auto', direction: 'ltr', mt: {xs: '2.7rem', sm: 0}, p: 0 }}>
                    {
                        station?.translations[0].video && 
                        <ListItem>
                            <VideoPlayer 
                                video={station?.translations[0].video.id} 
                                withHorizontal={false} 
                                simpleControls={true}
                                aspectRatio={'16/9'}
                            />
                        </ListItem>
                    }
                    {!station?.translations[0].video && (
                    <ListItem dir={direction} sx={{textAlign: 'start'}}>
                        <Box sx={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                            <ImageWrapper img={station?.information_photo ?? ''} height='22em' objectFit='contain' />
                        </Box>
                    </ListItem>
                    )}
                    {
                        station?.translations[0].audio && 
                        <ListItem>
                            <AudioPlayer audio={station?.translations[0].audio.id} name={station?.translations[0].audio.title ?? ''}/>
                        </ListItem>
                    }
                    {
                        station?.translations[0].information_description && 
                        <ListItem dir={direction}>
                            <TextWidget text={station?.translations[0].information_description}/>
                        </ListItem>
                    }
                    {station?.translations[0].links && station.translations[0].links.length > 0 && (
                        <ListItem dir={direction}>
                            <StationLinks links={station.translations[0].links} direction={direction} />
                        </ListItem>
                    )}
                    
            </List>
        </Box>
    );
};

export default StationPageV2;
